import React from 'react';
import {IconButton, TextField, Tooltip} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import {apiSearchUnifiedTags} from '../../utils/api/tags';
import {getTagColor} from './tagColors';

// searchTagsFn: optional (search) => Promise<options> override for the autocomplete catalog.
// Defaults to the unified tag catalog (/api/search/tags) which covers both corpus and DiTag
// stores — so every tag visible in the filter picker can also be typed in any result card's
// add-tag input, regardless of which store the card belongs to.
export default function NewTagInput({createTag, isPosting, existingTags = [], docId, searchTagsFn = null}) {
    const [showNewTagInput, setShowNewTagInput] = React.useState(false);
    const [newTag, setNewTag] = React.useState('');
    const [options, setOptions] = React.useState([]); // array of strings or tag objects
    const [loading, setLoading] = React.useState(false);
    const wrapperRef = React.useRef(null);
    const debounceRef = React.useRef(null);

    // build set of existing tag names (lowercased) for quick checks
    const existingNames = React.useMemo(() => {
        const s = new Set();
        for (const t of existingTags || []) {
            if (!t) continue;
            const name = (typeof t === 'string' ? t : (t.name || t.tag || '')).toString().trim().toLowerCase();
            if (name) s.add(name);
        }
        return s;
    }, [existingTags]);

    // Normalize options but preserve object shape when present (so we keep tagId, color, usageCount)
    const normalizedOptions = React.useMemo(() => {
        const seen = new Set();
        const list = [];
        for (const it of options || []) {
            if (!it) continue;
            if (typeof it === 'string') {
                const name = it.trim();
                const key = name.toLowerCase();
                if (!key || seen.has(key)) continue;
                seen.add(key);
                list.push({name, __alreadyAdded: existingNames.has(key)});
            } else if (typeof it === 'object') {
                const name = (it.name || it.tag || '').toString().trim();
                const key = name.toLowerCase();
                if (!key || seen.has(key)) continue;
                seen.add(key);
                // keep full object so caller can inspect tagId/color if needed
                list.push({...it, name, __alreadyAdded: existingNames.has(key)});
            }
        }
        return list;
    }, [options, existingNames]);

    const searchCatalog = React.useCallback(
        (query) => (searchTagsFn ? searchTagsFn(query) : apiSearchUnifiedTags(query)),
        [searchTagsFn],
    );

    // Load top 5 on open
    React.useEffect(() => {
        if (showNewTagInput) {
            let cancelled = false;
            (async () => {
                try {
                    setLoading(true);
                    const res = await searchCatalog('');
                    if (!cancelled) setOptions(res || []);
                } catch (_) {
                    if (!cancelled) setOptions([]);
                } finally {
                    if (!cancelled) setLoading(false);
                }
            })();
            return () => {
                cancelled = true;
            };
        }
    }, [showNewTagInput, searchCatalog]);

    // Debounced search when user types
    React.useEffect(() => {
        if (!showNewTagInput) return;
        if (showNewTagInput && newTag.trim().length > 0) {
            // Clear any pending timer
            if (debounceRef.current) clearTimeout(debounceRef.current);
            // Immediately clear current options and show loading while we wait for server
            setOptions([]);
            setLoading(true);
            let cancelled = false;
            debounceRef.current = setTimeout(async () => {
                try {
                    const res = await searchCatalog(newTag.trim());
                    if (!cancelled) setOptions(res || []);
                } catch (_) {
                    if (!cancelled) setOptions([]);
                } finally {
                    if (!cancelled) setLoading(false);
                }
            }, 250);
            return () => {
                cancelled = true;
                if (debounceRef.current) clearTimeout(debounceRef.current);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newTag, showNewTagInput, searchCatalog]);

    const closeInput = React.useCallback(() => {
        setShowNewTagInput(false);
        setNewTag('');
        setOptions([]);
    }, []);

    const handleCreate = React.useCallback(async (tagName) => {
        const t = (tagName || '').trim();
        if (!t) return;
        try {
            await createTag(t);
            closeInput();
        } catch (_) {
            // leave input open to allow retry
        }
    }, [createTag, closeInput]);

    // Build computed options: preserve tag objects and append create option last when input exists
    const computedOptions = React.useMemo(() => {
        const list = [...normalizedOptions];
        const input = newTag.trim();
        // If input exists and there isn't already an option with the same name (case-insensitive), add create option
        if (input) {
            const lower = input.toLowerCase();
            // also treat input as existing if it's already present in existingTags
            const exists = existingNames.has(lower) || list.some((opt) => {
                if (!opt) return false;
                if (typeof opt === 'string') return opt.toLowerCase() === lower;
                if (opt.__create) return false;
                return (opt.name || '').toLowerCase() === lower;
            });
            if (!exists) {
                list.push({__create: true, label: `Create "${input}"`, value: input});
            }
        }
        return list;
    }, [normalizedOptions, newTag, existingNames]);

    const getOptionDisabled = (opt) => {
        if (!opt) return false;
        if (opt.__create) return false;
        return !!opt.__alreadyAdded;
    };

    const getOptionLabel = (opt) => {
        if (!opt) return '';
        if (typeof opt === 'string') return opt;
        if (opt.__create) return opt.label;
        // server objects have .name
        return opt.name || '';
    };

    const getFirstSelectable = () => {
        for (const opt of computedOptions) {
            if (!opt) continue;
            if (typeof opt === 'string') {
                // strings shouldn't be disabled in our normalized flow, but check anyway
                if (!existingNames.has(opt.toLowerCase())) return opt;
                continue;
            }
            if (opt.__create) continue;
            // object with name
            if (opt.name && !opt.__alreadyAdded) return opt.name;
        }
        const input = newTag.trim();
        if (input && !existingNames.has(input.toLowerCase())) return input;
        return '';
    };

    return (
        <React.Fragment>
            {!showNewTagInput ? (
                <IconButton size={'small'} aria-label={'add-tag'} onClick={() => setShowNewTagInput(true)}>
                    <AddIcon/>
                </IconButton>
            ) : (
                <div ref={wrapperRef}>
                    <Autocomplete
                        freeSolo={false}
                        disableClearable
                        disabled={isPosting}
                        loading={loading}
                        openOnFocus
                        sx={{minWidth: 160}}
                        options={computedOptions}
                        filterOptions={(x) => x} // server-side filtering
                        autoHighlight
                        selectOnFocus
                        onClose={() => {
                            setShowNewTagInput(false);
                        }}
                        getOptionDisabled={getOptionDisabled}
                        onChange={(e, value) => {
                            if (!value) return;
                            if (typeof value === 'string') {
                                handleCreate(value);
                            } else if (value.__create) {
                                handleCreate(value.value);
                            } else if (value.__alreadyAdded) {
                                // already added -> ignore
                                return;
                            } else if (value.name) {
                                handleCreate(value.name);
                            }
                        }}
                        getOptionLabel={getOptionLabel}
                        renderOption={(props, option) => {
                            const label = getOptionLabel(option);
                            if (option && option.__alreadyAdded) {
                                return (
                                    <li {...props} key={label} style={{opacity: 0.6}}>
                                        <Tooltip title={"Already added"}>
                                            <span aria-disabled={"true"} style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                width: '100%',
                                                pointerEvents: 'none'
                                            }}>
                                                <span style={{
                                                    width: 8, height: 8, borderRadius: '50%',
                                                    backgroundColor: getTagColor(option), flexShrink: 0
                                                }}/>
                                                {label}
                                            </span>
                                        </Tooltip>
                                    </li>
                                );
                            }
                            if (option && option.__create) {
                                return (
                                    <li {...props} key={label}>
                                        {label}
                                    </li>
                                );
                            }
                            return (
                                <li {...props} key={label} style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                    <span style={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        backgroundColor: getTagColor(option), flexShrink: 0
                                    }}/>
                                    {label}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size={'small'}
                                placeholder={'Add tag'}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        // prevent form submit / default handling
                                        e.preventDefault();
                                        const first = getFirstSelectable();
                                        if (first) handleCreate(first);
                                    }
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    width: '120px'
                                }}
                            />
                        )}
                        inputValue={newTag}
                        onInputChange={(e, value) => setNewTag(value)}
                        ListboxProps={{
                            'aria-label': 'tags-suggestions'
                        }}
                    />
                </div>
            )}
        </React.Fragment>
    );
}
